import { DateTime } from "luxon";
import type React from "react";
import { type SyntheticEvent, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "../../../contexts/AuthProvider";
import { useChosenDepartment } from "../../../contexts/ChosenDepartment";
import { calculateShiftTimes } from "../../../contexts/utils/shift-times";
import { useCalculateRecurringShiftDailyHours } from "../../../hooks/useCalculateRecurringShiftDailyHours";
import { useDepartmentFlags } from "../../../hooks/useDepartmentFlags";
import { useErrorSnackbar } from "../../../hooks/useErrorSnackbar";
import { useJobs } from "../../../hooks/useJobs";
import { useFormatPersonName } from "../../../hooks/useNameFormat";
import { useRosterLookup } from "../../../hooks/useRosterLookup";
import { localStorageService } from "../../../services/LocalStorageService";
import { trackEvent } from "../../../utils/analytics";
import { getWeekStart } from "../../../utils/dateUtils";
import { isNurseActiveInDepartment } from "../../../utils/formatters";
import { getEnabledNurseJobs } from "../../../utils/jobs";
import {
	ChangesSource,
	type ConsecutiveDaysRecurring,
	type CreateRecurringShiftRequest,
	type DailyRecurring,
	type DepartmentLocation,
	type Nurse,
	RecurringShiftRecurrenceType,
	type UpdateRecurringShiftRequest,
	type WeeklyRecurring,
} from "../../../utils/models";
import { enumToDropdownItems, getUniqueLocations } from "../../../utils/utilFunctions";
import { DIFF_HOURS_WARNING } from "../../../utils/warningDialogs/utils";
import { useNurseWarnings } from "../../../utils/warningDialogs/WarningDialog";
import type { SystemAutoCompleteOption } from "../../UIToolkit/Inputs/SystemAutoComplete/SystemAutoComplete";
import type { DropdownItem } from "../../UIToolkit/Inputs/SystemDropDown";
import type { ValidationTextFieldProps } from "../../UIToolkit/Inputs/SystemValidationTextField";
import { RecurringShiftComponentSource } from "../RecurringShift";
import { DaysInWeek } from "./DaysInWeekSelector";
import { useCreateRecurringShift, useGetRecurringShift, useUpdateRecurringShift } from "./hooks";

const noLocationText = "No Location";

/**
 * The global axios response interceptor (`convertIsoDates`) rewrites bare
 * `yyyy-MM-dd` strings into Luxon `DateTime`s, so a fetched recurring shift's
 * `startDate`/`endDate` can arrive as EITHER a string (untouched) or a DateTime
 * (already converted). `shiftStart`/`shiftEnd` are `HH:mm:ss` and never match the
 * ISO regex, so they stay strings — but we accept a DateTime defensively too.
 * These helpers normalize both so edit-mode pre-fill never throws on
 * `DateTime.fromFormat(<DateTime>)`. The string path is byte-for-byte the
 * original behavior.
 */
const parseShiftDate = (value: string | DateTime): DateTime => (typeof value === "string" ? DateTime.fromFormat(value, "yyyy-MM-dd") : value);

const parseShiftTime = (value: string | DateTime, zone: string): DateTime =>
	typeof value === "string" ? DateTime.fromFormat(value, "HH:mm:ss", { zone }) : value.setZone(zone);
export type RecurringShiftRecurrenceTypeKeys = keyof typeof RecurringShiftRecurrenceType;
const recurringShiftRecurrenceTypeToStringSingle: Record<RecurringShiftRecurrenceType, string> = {
	[RecurringShiftRecurrenceType.Daily]: "Day",
	[RecurringShiftRecurrenceType.Weekly]: "Week",
	[RecurringShiftRecurrenceType.ConsecutiveDays]: "Rotating Schedule",
};
const recurringShiftRecurrenceTypeToStringPlural: Record<RecurringShiftRecurrenceType, string> = {
	[RecurringShiftRecurrenceType.Daily]: "Days",
	[RecurringShiftRecurrenceType.Weekly]: "Weeks",
	[RecurringShiftRecurrenceType.ConsecutiveDays]: "Rotating Schedule",
};
export const recurrenceTypeOptions: DropdownItem<RecurringShiftRecurrenceTypeKeys>[] = enumToDropdownItems(
	RecurringShiftRecurrenceType,
	(val) => recurringShiftRecurrenceTypeToStringPlural[val],
);

export type RecurringShiftData = {
	selectedNurse: SystemAutoCompleteOption<Nurse> | null;
	selectedLocation: DepartmentLocation | null | undefined;
	selectedJobId?: string;
	startDate?: DateTime;
	endDate?: DateTime;
	shiftStart?: DateTime;
	shiftEnd?: DateTime;
	recurrenceType: RecurringShiftRecurrenceTypeKeys;
	recurrenceValue: number;
	consecutiveValues: {
		workingDays: number;
		restDays: number;
	};
	selectedDays?: DaysInWeek[];
};

export enum RecurringShiftDisplayMode {
	Edit = "edit",
	Create = "create",
}

export const useRecurringShiftState = ({
	onClose,
	initialRecurringShiftData,
	recurringShiftDisplayMode,
	existingRecurringShiftId,
	existingShiftStartTime,
	existingShiftNurseId,
	clickedDate,
	source,
}: {
	onClose?: (didUpdateOrCreate?: boolean) => void;
	initialRecurringShiftData: RecurringShiftData;
	recurringShiftDisplayMode: RecurringShiftDisplayMode;
	existingShiftStartTime?: DateTime;
	existingShiftNurseId?: number;
	existingRecurringShiftId?: number;
	clickedDate: DateTime;
	source: RecurringShiftComponentSource;
}) => {
	const { chosenDepartment } = useChosenDepartment();
	const { timezone, isAnyDemo } = useDepartmentFlags();
	const showErrorSnackbar = useErrorSnackbar();
	const { roster } = useRosterLookup(chosenDepartment.department_id);
	const formatPersonName = useFormatPersonName();
	const enabledJobs = useJobs();
	const {
		userInfo: { id: userId },
	} = useAuth();
	const isUpdatingDueToEdit = useRef(false);
	const [recurringShiftData, setRecurringShiftData] = useState<RecurringShiftData>({
		...initialRecurringShiftData,
		startDate: clickedDate > DateTime.now() ? clickedDate.startOf("day") : DateTime.now().startOf("day"),
	});

	const { mutateAsync: createRecurringShift, isPending: createRecurringShiftLoading, isSuccess: createRecurringShiftSuccess } = useCreateRecurringShift();
	const { mutateAsync: updateRecurringShift, isPending: updateRecurringShiftLoading, isSuccess: updateRecurringShiftSuccess } = useUpdateRecurringShift();
	const [dateForUpdate, setDateForUpdate] = useState<DateTime>(existingShiftStartTime ?? DateTime.now());

	const apiRequest = useMemo<CreateRecurringShiftRequest | null>(() => {
		if (!recurringShiftData.selectedNurse || !recurringShiftData.startDate || !recurringShiftData.shiftStart || !recurringShiftData.shiftEnd) {
			return null;
		}
		const recurrenceCurrentType = RecurringShiftRecurrenceType[recurringShiftData.recurrenceType];
		return {
			recurringShift: {
				nurseId: recurringShiftData.selectedNurse.value.id,
				departmentId: chosenDepartment.department_id,
				deleted: false,
				startDate: getWeekStart(recurringShiftData.startDate, chosenDepartment.week_start_day).toFormat("yyyy-MM-dd"),
				endDate: recurringShiftData.endDate?.toFormat("yyyy-MM-dd") ?? undefined,
				shiftStart: recurringShiftData.shiftStart.toFormat("HH:mm"),
				shiftEnd: recurringShiftData.shiftEnd.toFormat("HH:mm"),
				location: recurringShiftData.selectedLocation?.location ?? undefined,
				jobId: recurringShiftData.selectedJobId,
				recurringParams:
					recurrenceCurrentType === RecurringShiftRecurrenceType.Daily
						? ({
								recurringType: recurringShiftData.recurrenceType,
								interval: recurringShiftData.recurrenceValue,
							} as DailyRecurring)
						: recurrenceCurrentType === RecurringShiftRecurrenceType.Weekly
							? ({
									recurringType: recurringShiftData.recurrenceType,
									interval: recurringShiftData.recurrenceValue,
									weekdays: recurringShiftData.selectedDays?.map((day) => day.toLowerCase()) ?? [],
								} as WeeklyRecurring)
							: ({
									recurringType: recurringShiftData.recurrenceType,
									working_days_amount: recurringShiftData.consecutiveValues.workingDays,
									rest_days_amount: recurringShiftData.consecutiveValues.restDays,
								} as ConsecutiveDaysRecurring),
			},
			source: source === RecurringShiftComponentSource.MasterSchedule ? ChangesSource.MasterSchedule : ChangesSource.Staffing,
		};
	}, [recurringShiftData, chosenDepartment.department_id, chosenDepartment.week_start_day, source]);

	const { data: exceedingDatesFromAPI, isLoading: isCalculatingDailyHours } = useCalculateRecurringShiftDailyHours(
		apiRequest,
		recurringShiftData.startDate,
		recurringShiftData.endDate,
	);

	const performSave = useCallback(async () => {
		const recurrenceCurrentType = RecurringShiftRecurrenceType[recurringShiftData.recurrenceType];
		const request: CreateRecurringShiftRequest = {
			recurringShift: {
				nurseId: recurringShiftData.selectedNurse?.value.id ?? -1,
				departmentId: chosenDepartment.department_id,
				deleted: false,
				startDate:
					(recurringShiftData.recurrenceType === "Weekly" && recurringShiftData.startDate
						? getWeekStart(recurringShiftData.startDate, chosenDepartment.week_start_day)
						: recurringShiftData.startDate
					)?.toFormat("yyyy-MM-dd") ?? "",
				endDate: recurringShiftData.endDate?.toFormat("yyyy-MM-dd") ?? undefined,
				shiftStart: recurringShiftData.shiftStart?.toFormat("HH:mm") ?? "",
				shiftEnd: recurringShiftData.shiftEnd?.toFormat("HH:mm") ?? "",
				location: recurringShiftData.selectedLocation?.location ?? undefined,
				jobId: recurringShiftData.selectedJobId,
				recurringParams:
					recurrenceCurrentType === RecurringShiftRecurrenceType.Daily
						? ({
								recurringType: recurringShiftData.recurrenceType,
								interval: recurringShiftData.recurrenceValue,
							} as DailyRecurring)
						: recurrenceCurrentType === RecurringShiftRecurrenceType.Weekly
							? ({
									recurringType: recurringShiftData.recurrenceType,
									interval: recurringShiftData.recurrenceValue,
									weekdays: recurringShiftData.selectedDays?.map((day) => day.toLowerCase()) ?? [],
								} as WeeklyRecurring)
							: ({
									recurringType: recurringShiftData.recurrenceType,
									working_days_amount: recurringShiftData.consecutiveValues.workingDays,
									rest_days_amount: recurringShiftData.consecutiveValues.restDays,
								} as ConsecutiveDaysRecurring),
			},
			source: source === RecurringShiftComponentSource.MasterSchedule ? ChangesSource.MasterSchedule : ChangesSource.Staffing,
		};
		if (recurringShiftDisplayMode === RecurringShiftDisplayMode.Create) {
			trackEvent("recurring_shift_create", request);
			try {
				await createRecurringShift(request);
			} catch {}
		} else if (dateForUpdate) {
			if (existingRecurringShiftId) {
				const updateRequest: UpdateRecurringShiftRequest = {
					...request,
					startDate: dateForUpdate.toFormat("yyyy-MM-dd"),
					recurringShift: {
						...request.recurringShift,
						id: existingRecurringShiftId,
					},
				};
				trackEvent("recurring_shift_update", updateRequest);
				try {
					await updateRecurringShift(updateRequest);
				} catch {}
			} else {
				showErrorSnackbar("We cannot update recurring shift without id", request, new Error("No recurring shift id"));
				return;
			}
		} else {
			showErrorSnackbar("We cannot update recurring shift without date selection", request, new Error("No date selected"));
		}
	}, [
		dateForUpdate,
		recurringShiftDisplayMode,
		createRecurringShift,
		chosenDepartment.department_id,
		chosenDepartment.week_start_day,
		recurringShiftData,
		showErrorSnackbar,
		existingRecurringShiftId,
		updateRecurringShift,
		source,
	]);

	const [warningState, checkWarnings, _, __, handleDailyHoursConfirm, handleCancelWarnings] = useNurseWarnings(performSave, chosenDepartment.longShiftDuration);

	/**
	 * This ref is used to determine if the user has manually changed the shift start and end times - if they were manually set(or initially set) we do not change them even if the nurse or location changes.
	 */
	const didUserChangeHoursManually = useRef(!!initialRecurringShiftData.shiftStart && !!initialRecurringShiftData.shiftEnd);

	const nursesList: SystemAutoCompleteOption<Nurse>[] = useMemo(
		() =>
			// DEFAULT: no staff to pick until the roster loads. This legacy panel renders only for a demo user who
			// switches versions by hand, after the page has loaded the roster.
			(roster ?? [])
				.filter(
					(nurse) =>
						isNurseActiveInDepartment(nurse, chosenDepartment.department_id) &&
						(!recurringShiftData.selectedLocation ||
							recurringShiftData.selectedLocation.roles.includes("all") ||
							recurringShiftData.selectedLocation.roles.includes(nurse.role)),
				)
				.map<SystemAutoCompleteOption<Nurse>>((value) => ({
					label: formatPersonName(value),
					value: value,
					disabled:
						!!value.scheduledDeactivationDate &&
						!!recurringShiftData.startDate &&
						value.scheduledDeactivationDate.startOf("day") < recurringShiftData.startDate.startOf("day"),
				})),
		[roster, recurringShiftData.startDate, recurringShiftData.selectedLocation, chosenDepartment.department_id, formatPersonName],
	);
	const locations = useMemo(
		() =>
			[null, ...getUniqueLocations(chosenDepartment.locations, false).filter((value) => value.location)].map<DropdownItem<DepartmentLocation | null>>(
				(value) => ({
					label: () => value?.location ?? noLocationText,
					value: value,
					id: value?.id ?? noLocationText,
				}),
			),
		[chosenDepartment],
	);

	const editModeFoundNurse: SystemAutoCompleteOption<Nurse> | null = useMemo(() => {
		return recurringShiftDisplayMode === RecurringShiftDisplayMode.Edit ? nursesList.find((value) => value.value.id === existingShiftNurseId) || null : null;
	}, [recurringShiftDisplayMode, existingShiftNurseId, nursesList]);
	const boxRef = useRef<HTMLDivElement>(null);
	const [timeStartEndHeight, setTimeStartEndHeight] = useState<number | "auto">("auto");
	const [warning, setWarning] = useState<"Save" | "Cancel" | null>(null);
	const [editSavingDateFromValid, setEditSavingDateFromValid] = useState<boolean>(true);

	useLayoutEffect(() => {
		if (boxRef.current) {
			setTimeStartEndHeight(boxRef.current.offsetHeight);
		}
	}, []);

	const nurseListChangeHandler = useCallback(
		(_: SyntheticEvent, value: SystemAutoCompleteOption<Nurse> | null) => {
			const nurseJobs = value?.value ? getEnabledNurseJobs(value.value, enabledJobs) : [];
			setRecurringShiftData((prevState) => ({
				...prevState,
				selectedNurse: value,
				selectedJobId: nurseJobs.length === 1 ? nurseJobs[0]?.id : undefined,
			}));
		},
		[enabledJobs],
	);

	useEffect(() => {
		if (didUserChangeHoursManually.current) {
			return; //We dont change the times if they are already set
		}
		if (recurringShiftData.selectedNurse) {
			if (isUpdatingDueToEdit.current) {
				isUpdatingDueToEdit.current = false;
				return;
			}
			const { startTime: calcStartTime, endTime: calcEndTime } = calculateShiftTimes({
				day: { date: clickedDate },
				shiftNo: 0,
				nurseRole: recurringShiftData.selectedNurse.value.role,
				chosenDepartment,
				location: recurringShiftData.selectedLocation?.location,
			});
			setRecurringShiftData((prevState) => ({
				...prevState,
				shiftStart: calcStartTime,
				shiftEnd: calcEndTime,
			}));
		}
	}, [recurringShiftData.selectedNurse, clickedDate, chosenDepartment, recurringShiftData.selectedLocation]);

	const locationChangeHandler = useCallback((value: DepartmentLocation | null | undefined) => {
		setRecurringShiftData((prevState) => ({
			...prevState,
			selectedLocation: value,
		}));
	}, []);

	useEffect(() => {
		if (source === RecurringShiftComponentSource.MasterSchedule && recurringShiftData.recurrenceType === "Weekly") {
			let relevantDate = getWeekStart(DateTime.now().setZone(timezone), chosenDepartment.week_start_day);
			if (recurringShiftData.recurrenceValue > 1) {
				relevantDate = getWeekStart(clickedDate, chosenDepartment.week_start_day);
			}
			if (recurringShiftDisplayMode === RecurringShiftDisplayMode.Create) {
				setRecurringShiftData((prevState) => ({
					...prevState,
					startDate: relevantDate,
				}));
			}
			setDateForUpdate(relevantDate);
		}
	}, [
		source,
		recurringShiftDisplayMode,
		recurringShiftData.recurrenceValue,
		clickedDate,
		timezone,
		recurringShiftData.recurrenceType,
		chosenDepartment.week_start_day,
	]);

	const startDateValue = useMemo(
		() =>
			(recurringShiftData.recurrenceType === "Weekly" && recurringShiftData.startDate
				? getWeekStart(recurringShiftData.startDate, chosenDepartment.week_start_day)
				: recurringShiftData.startDate
			)?.toFormat("yyyy-MM-dd") ?? "",
		[recurringShiftData.startDate, recurringShiftData.recurrenceType, chosenDepartment.week_start_day],
	);
	const endDateValue = useMemo(() => recurringShiftData.endDate?.toFormat("yyyy-MM-dd") ?? "", [recurringShiftData.endDate]);
	const startTimeValue = useMemo(() => recurringShiftData.shiftStart?.toFormat("h:mm a") ?? "", [recurringShiftData.shiftStart]);
	const endTimeValue = useMemo(() => recurringShiftData.shiftEnd?.toFormat("h:mm a") ?? "", [recurringShiftData.shiftEnd]);
	const [isEditingTime, setIsEditingTime] = useState<boolean>(false);
	const [anchorRef, setAnchorRef] = useState<HTMLElement>();

	const toggleIsEditingTime = useCallback(() => {
		setIsEditingTime((prevState) => !prevState);
	}, []);

	const editTimePickerClicked = useCallback(
		(event: React.MouseEvent<HTMLElement>) => {
			setAnchorRef(event.currentTarget);
			toggleIsEditingTime();
		},
		[toggleIsEditingTime],
	);

	const startDateChangeHandler = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
		const newDate = DateTime.fromFormat(event.target.value, "yyyy-MM-dd");
		setRecurringShiftData((prevState) => ({
			...prevState,
			startDate: newDate.isValid ? newDate : undefined,
		}));
	}, []);
	const startDateEnabled = useMemo(() => {
		return recurringShiftDisplayMode === RecurringShiftDisplayMode.Create;
	}, [recurringShiftDisplayMode]);

	const startDateValidationCallback = useCallback(
		(value: string) => {
			if (!startDateEnabled) return true; // If start date is not enabled, skip validation
			const startDate = DateTime.fromFormat(value, "yyyy-MM-dd");
			return startDate.isValid && startDate > DateTime.now().endOf("day").minus({ weeks: 1 }).minus({ days: 1 });
		},
		[startDateEnabled],
	);
	const startDateValidation: ValidationTextFieldProps = useMemo(
		() => ({
			validationCallback: startDateValidationCallback,
			helperText: {
				valid: "Start date is valid",
				invalid: "Start date cant be in the past",
			},
			showValidWhenValid: "never",
			performValidation: "onlyAfterInitialChange",
		}),
		[startDateValidationCallback],
	);

	const endDateChangeHandler = useCallback((date: string | DateTime) => {
		const newDate = date instanceof DateTime ? date : DateTime.fromFormat(date, "yyyy-MM-dd");
		setRecurringShiftData((prevState) => ({
			...prevState,
			endDate: newDate.isValid ? newDate : undefined,
		}));
	}, []);

	const clearEndDate = useCallback(() => {
		setRecurringShiftData((prevState) => ({
			...prevState,
			endDate: undefined,
		}));
	}, []);

	const endDateValidationCallback = useCallback(
		(value: string) => {
			if (!recurringShiftData.startDate) return true;
			const endDate = DateTime.fromFormat(value, "yyyy-MM-dd");
			return endDate.isValid ? endDate > recurringShiftData.startDate : true;
		},
		[recurringShiftData.startDate],
	);
	const endDateValidation: ValidationTextFieldProps = useMemo(
		() => ({
			validationCallback: endDateValidationCallback,
			helperText: {
				valid: "End date is valid",
				invalid: "End date must be after start date",
			},
			showValidWhenValid: "never",
			performValidation: "onlyAfterInitialChange",
		}),
		[endDateValidationCallback],
	);

	const shiftDurationString = useMemo(() => {
		return `${
			recurringShiftData.shiftStart && recurringShiftData.shiftEnd
				? `${Math.abs(recurringShiftData.shiftEnd.diff(recurringShiftData.shiftStart).as("hours"))} hours`
				: "0 hours"
		}`;
	}, [recurringShiftData.shiftStart, recurringShiftData.shiftEnd]);
	const recurrenceValue = useMemo(() => {
		return recurringShiftData.recurrenceValue?.toString() ?? "1";
	}, [recurringShiftData.recurrenceValue]);
	const recurrenceChangeHandler = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
		setRecurringShiftData((prevState) => ({
			...prevState,
			recurrenceValue: event.target.value ? (Number.parseInt(event.target.value, 10) >= 1 ? Number.parseInt(event.target.value, 10) : 1) : 1,
		}));
	}, []);

	const consecutiveDaysWorkingDaysValue = useMemo(
		() => recurringShiftData.consecutiveValues.workingDays?.toString() ?? "1",
		[recurringShiftData.consecutiveValues.workingDays],
	);
	const consecutiveDaysRestDaysValue = useMemo(
		() => recurringShiftData.consecutiveValues.restDays?.toString() ?? "1",
		[recurringShiftData.consecutiveValues.restDays],
	);
	const consecutiveDaysChangeHandler = useCallback((event: React.ChangeEvent<HTMLInputElement>, type: "working" | "rest") => {
		const value = event.target.value ? (Number.parseInt(event.target.value, 10) >= 1 ? Number.parseInt(event.target.value, 10) : 1) : 1;
		setRecurringShiftData((prevState) => ({
			...prevState,
			consecutiveValues: {
				...prevState.consecutiveValues,
				...(type === "working" ? { workingDays: value } : { restDays: value }),
			},
		}));
	}, []);

	const consecutiveDaysWorkingDaysChangeHandler = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			consecutiveDaysChangeHandler(event, "working");
		},
		[consecutiveDaysChangeHandler],
	);
	const consecutiveDaysRestDaysChangeHandler = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			consecutiveDaysChangeHandler(event, "rest");
		},
		[consecutiveDaysChangeHandler],
	);

	const recurrenceTypeChangeHandler = useCallback(
		(selectedValue: RecurringShiftRecurrenceTypeKeys | undefined) => {
			if (selectedValue) {
				setRecurringShiftData((prevState) => ({
					...prevState,
					recurrenceType: selectedValue,
					startDate: selectedValue === "ConsecutiveDays" ? clickedDate : prevState.startDate,
				}));
				if (selectedValue === "Daily") {
					setRecurringShiftData((prevState) => ({
						...prevState,
						selectedDays: undefined, // Clear selected days for daily recurrence
					}));
				}
				localStorageService.setRecurringShiftSavedRecurrenceType(selectedValue, userId);
			}
		},
		[clickedDate, userId],
	);

	const daysInWeekChangeHandler = useCallback((selectedDays: DaysInWeek[]) => {
		setRecurringShiftData((prevState) => ({
			...prevState,
			selectedDays: selectedDays,
		}));
	}, []);

	const summaryString = useMemo(() => {
		const endsString = ` | Ends - ${recurringShiftData.endDate?.toJSDate().toLocaleDateString() ?? "never"}`;
		if (recurringShiftData.recurrenceType === "ConsecutiveDays") {
			return `${recurringShiftData.consecutiveValues.workingDays} working day${recurringShiftData.consecutiveValues.workingDays > 1 ? "s" : ""} followed by ${recurringShiftData.consecutiveValues.restDays} rest day${recurringShiftData.consecutiveValues.restDays > 1 ? "s" : ""} ${endsString}`;
		}
		return `Occurs every ${recurringShiftData.recurrenceValue ?? "-"} ${
			recurringShiftData.recurrenceType
				? recurringShiftRecurrenceTypeToStringSingle[RecurringShiftRecurrenceType[recurringShiftData.recurrenceType]]
				: recurringShiftRecurrenceTypeToStringPlural[RecurringShiftRecurrenceType[recurringShiftData.recurrenceType]]
		}${recurringShiftData.selectedDays && recurringShiftData.selectedDays.length > 0 ? ` on ${recurringShiftData.selectedDays.length >= 7 ? "all days" : `${recurringShiftData.selectedDays?.join(", ")}`}` : ""} ${endsString}`;
	}, [
		recurringShiftData.recurrenceValue,
		recurringShiftData.recurrenceType,
		recurringShiftData.selectedDays,
		recurringShiftData.endDate,
		recurringShiftData.consecutiveValues,
	]);

	const {
		data: fetchedShiftData,
		isFetching,
		isFetched,
	} = useGetRecurringShift(chosenDepartment.department_id, existingShiftNurseId, existingRecurringShiftId);

	useEffect(() => {
		if (isFetched && fetchedShiftData) {
			const selectedDaysMapping =
				fetchedShiftData.recurringParams.recurringType === "Weekly"
					? (fetchedShiftData.recurringParams as WeeklyRecurring).weekdays.map<DaysInWeek>((value) => {
							return DaysInWeek[value.toLowerCase() as keyof typeof DaysInWeek];
						})
					: undefined;
			isUpdatingDueToEdit.current = true;
			didUserChangeHoursManually.current = !!fetchedShiftData.shiftStart && !!fetchedShiftData.shiftEnd;
			setRecurringShiftData((prevState) => ({
				...prevState,
				shiftStart: parseShiftTime(fetchedShiftData.shiftStart, timezone),
				shiftEnd: parseShiftTime(fetchedShiftData.shiftEnd, timezone),
				startDate: parseShiftDate(fetchedShiftData.startDate),
				endDate: fetchedShiftData.endDate ? parseShiftDate(fetchedShiftData.endDate) : undefined,
				recurrenceValue: fetchedShiftData.recurringParams.interval,
				recurrenceType: (recurrenceTypeOptions.find((value) => value.value === fetchedShiftData.recurringParams.recurringType) ?? recurrenceTypeOptions[0])
					.value,
				selectedDays: selectedDaysMapping,
				selectedLocation: locations.find((value) => value.value?.location === fetchedShiftData.location)?.value,
				selectedJobId: fetchedShiftData.jobId,
				...(fetchedShiftData.recurringParams.recurringType === "ConsecutiveDays"
					? {
							consecutiveValues: {
								workingDays: (fetchedShiftData.recurringParams as ConsecutiveDaysRecurring).working_days_amount,
								restDays: (fetchedShiftData.recurringParams as ConsecutiveDaysRecurring).rest_days_amount,
							},
						}
					: {}),
			}));
		}
	}, [fetchedShiftData, isFetched, locations, timezone]);

	useEffect(() => {
		if (createRecurringShiftSuccess || updateRecurringShiftSuccess) {
			onClose?.(true);
		}
	}, [createRecurringShiftSuccess, updateRecurringShiftSuccess, onClose]);

	const onCancelClick = useCallback(() => {
		setWarning("Cancel");
	}, []);

	const datesExceedingLimit = useMemo(() => {
		if (!exceedingDatesFromAPI || exceedingDatesFromAPI.length === 0) {
			return [];
		}
		return exceedingDatesFromAPI.map((date) => {
			return date.toFormat("MMM D, yyyy");
		});
	}, [exceedingDatesFromAPI]);

	const onSaveClick = useCallback(() => {
		if (datesExceedingLimit.length > 0) {
			checkWarnings(false, undefined, undefined, (chosenDepartment.longShiftDuration ?? DIFF_HOURS_WARNING) + 1, false);
		} else {
			setWarning("Save");
		}
	}, [datesExceedingLimit, checkWarnings, chosenDepartment.longShiftDuration]);

	const onWarningClose = useCallback(() => {
		setWarning(null);
	}, []);

	const onCancelConfirm = useCallback(() => {
		onWarningClose();
		onClose?.();
	}, [onClose, onWarningClose]);

	const onSaveConfirm = useCallback(async () => {
		onWarningClose();
		await performSave();
	}, [onWarningClose, performSave]);

	const handleTimePickerValueChange = useCallback(({ newStartTime, newEndTime }: { newStartTime: DateTime; newEndTime: DateTime }) => {
		didUserChangeHoursManually.current = true; // User manually changed the time
		setRecurringShiftData((prevState) => ({
			...prevState,
			shiftStart: newStartTime,
			shiftEnd: newEndTime,
		}));
	}, []);

	const onEditDateSelectionChange = useCallback((date: DateTime) => {
		setDateForUpdate(date);
	}, []);

	const onEditDateSelectionChangeValid = useCallback((isValid: boolean) => {
		setEditSavingDateFromValid(isValid);
	}, []);

	const saveLoading = useMemo(() => {
		return createRecurringShiftLoading || updateRecurringShiftLoading || isCalculatingDailyHours;
	}, [createRecurringShiftLoading, updateRecurringShiftLoading, isCalculatingDailyHours]);

	const saveEnabled = useMemo(() => {
		return (
			recurringShiftData.selectedNurse &&
			startDateValidationCallback(startDateValue) &&
			recurringShiftData.shiftStart &&
			recurringShiftData.shiftEnd &&
			recurringShiftData.recurrenceType &&
			(((recurringShiftData.recurrenceType === "Daily" || recurringShiftData.recurrenceType === "Weekly") && recurringShiftData.recurrenceValue > 0) ||
				recurringShiftData.recurrenceType === "ConsecutiveDays") &&
			(recurringShiftData.recurrenceType === "Daily" ||
				(recurringShiftData.recurrenceType === "Weekly" && recurringShiftData.selectedDays && recurringShiftData.selectedDays.length > 0) ||
				(recurringShiftData.recurrenceType === "ConsecutiveDays" &&
					recurringShiftData.consecutiveValues.workingDays > 0 &&
					recurringShiftData.consecutiveValues.restDays > 0)) &&
			(!recurringShiftData.endDate || endDateValidationCallback(endDateValue)) &&
			(recurringShiftDisplayMode === RecurringShiftDisplayMode.Create ||
				(recurringShiftDisplayMode === RecurringShiftDisplayMode.Edit && editSavingDateFromValid))
		);
	}, [
		recurringShiftData.selectedNurse,
		startDateValidationCallback,
		recurringShiftData.shiftStart,
		recurringShiftData.shiftEnd,
		recurringShiftData.recurrenceValue,
		recurringShiftData.selectedDays,
		startDateValue,
		endDateValidationCallback,
		recurringShiftData.recurrenceType,
		recurringShiftData.endDate,
		endDateValue,
		recurringShiftData.consecutiveValues,
		editSavingDateFromValid,
		recurringShiftDisplayMode,
	]);

	const nurseSelectionDisabled = useMemo(
		() => recurringShiftDisplayMode === RecurringShiftDisplayMode.Edit || initialRecurringShiftData.selectedNurse !== null,
		[recurringShiftDisplayMode, initialRecurringShiftData.selectedNurse],
	);

	useEffect(() => {
		if (recurringShiftDisplayMode === RecurringShiftDisplayMode.Edit && editModeFoundNurse && recurringShiftData.selectedNurse !== editModeFoundNurse) {
			setRecurringShiftData((prev) => ({
				...prev,
				selectedNurse: editModeFoundNurse,
			}));
		} else if (
			recurringShiftDisplayMode === RecurringShiftDisplayMode.Create &&
			initialRecurringShiftData.selectedNurse &&
			nursesList.length > 0 &&
			(!recurringShiftData.selectedNurse || recurringShiftData.selectedNurse.value.id !== initialRecurringShiftData.selectedNurse.value.id)
		) {
			const found: SystemAutoCompleteOption<Nurse> | null =
				nursesList.find((value) => value.value.id === initialRecurringShiftData.selectedNurse?.value.id) || null;
			if (found !== recurringShiftData.selectedNurse) {
				setRecurringShiftData((prev) => ({ ...prev, selectedNurse: found }));
			}
		}
	}, [recurringShiftDisplayMode, editModeFoundNurse, nursesList, initialRecurringShiftData.selectedNurse, recurringShiftData.selectedNurse]);

	const maximumValueForRecurrence = useMemo(
		() => (source === RecurringShiftComponentSource.MasterSchedule ? chosenDepartment.master_schedule_weeks : null),
		[source, chosenDepartment.master_schedule_weeks],
	);

	return {
		recurringShiftData,
		nursesList,
		locations,
		boxRef,
		timeStartEndHeight,
		nurseListChangeHandler,
		recurrenceTypeOptions: isAnyDemo ? recurrenceTypeOptions : [recurrenceTypeOptions.find((value) => value.value === "Weekly") ?? recurrenceTypeOptions[2]],
		noLocationText,
		locationChangeHandler,
		startDateValue,
		endDateValue,
		startTimeValue,
		endTimeValue,
		startDateChangeHandler,
		startDateValidation,
		endDateChangeHandler,
		endDateValidation,
		shiftDurationString,
		recurrenceValue,
		recurrenceChangeHandler,
		recurrenceTypeChangeHandler,
		daysInWeekChangeHandler,
		summaryString,
		onCancelClick,
		onSaveClick,
		onCancelConfirm,
		onSaveConfirm,
		warning,
		onWarningClose,
		saveEnabled,
		editTimePickerClicked,
		anchorRef,
		isEditingTime,
		handleTimePickerValueChange,
		toggleIsEditingTime,
		onEditDateSelectionChange,
		isFetching,
		saveLoading,
		consecutiveDaysWorkingDaysValue,
		consecutiveDaysRestDaysValue,
		consecutiveDaysWorkingDaysChangeHandler,
		consecutiveDaysRestDaysChangeHandler,
		startDateEnabled,
		nurseSelectionDisabled,
		selectedJobId: recurringShiftData.selectedJobId,
		setSelectedJobId: (jobId: string | undefined) => setRecurringShiftData((prev) => ({ ...prev, selectedJobId: jobId })),
		clearEndDate,
		onEditDateSelectionChangeValid,
		maximumValueForRecurrence,
		dateForUpdate,
		warningState,
		datesExceedingLimit,
		handleDailyHoursConfirm,
		handleCancelWarnings,
	};
};
